import { useCallback, useContext } from 'react';
import useCallbackAsync from './useCallbackAsync';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import { useTranslation } from 'react-i18next';
import AuthContext from 'providers/Auth/Auth.context';
import { LOGIN_ROUTE } from 'data/global';
import { useNavigate } from 'react-router-dom';
import mixpanelService from 'services/mixpanelService';
import { ListingWatchedInitiatedEventProps } from 'models/MixpanelEvents';
import useGetMixpanelProps from './useGetMixpanelProps';

export default function useWatchlist(
  listingId: string,
  dealId: string,
  isWatchlisted: boolean,
  onWatchedListed: (value: boolean, listingId: string) => void,
) {
  const { popSuccess } = useContext(PopNotificationsContext);
  const { isLoggedIn } = useContext(AuthContext);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const getMixpanelProps = useGetMixpanelProps();

  const navigateToLoginPage = useCallback(
    () => navigate(LOGIN_ROUTE),
    [navigate],
  );

  const [toggleWatchList, loading] = useCallbackAsync(async () => {
    if (!listingId) return;

    onWatchedListed(!isWatchlisted, listingId);

    if (!isWatchlisted) {
      mixpanelService.listingWathchedInitiated(
        getMixpanelProps({
          dealId,
        }) as ListingWatchedInitiatedEventProps,
      );
    }

    await api.listings[isWatchlisted ? 'removeWatchlist' : 'addWatchlist'](
      listingId,
    );

    popSuccess(
      t(
        SUCCESS_MESSAGES[
          isWatchlisted ? 'REMOVED_FROM_WATCHLIST' : 'ADDED_TO_WATCHLIST'
        ],
      ),
    );
  });

  const onWatchlist = useCallback(
    () => (isLoggedIn ? toggleWatchList() : navigateToLoginPage()),
    [isLoggedIn, navigateToLoginPage, toggleWatchList],
  );

  return { toggleWatchList, loading, onWatchlist };
}
